import React, { useEffect, useState } from 'react';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import {
  Banknote, Smartphone, TrendingUp, TrendingDown, ArrowDownRight, Clock,
  ChevronLeft, ChevronRight,
} from 'lucide-react';
import api from '../../../services/api';
import { RecetteNette as RecetteNetteData } from '../../../types';

type Periode = { start_date?: string; end_date?: string };

interface Props {
  dateRange: Periode;
  /** Remonte la période choisie (navigation par mois) au tableau de bord, qui
   *  pilote aussi le filtre de dates : les deux restent d'accord. */
  onPeriodeChange?: (periode: Periode) => void;
}

const fmtGNF = (n: number) =>
  new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(n) + ' GNF';

const fmtNombre = (n: number) =>
  new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(n);

// Un taux se lit « 63,1 % » en français, pas « 63.1 % »
const fmtTaux = (n: number) =>
  new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 1 }).format(n);

const fmtShort = (n: number) => {
  const abs = Math.abs(n);
  const signe = n < 0 ? '-' : '';
  if (abs >= 1_000_000_000) return `${signe}${(abs / 1_000_000_000).toFixed(1)} Md`;
  if (abs >= 1_000_000) return `${signe}${(abs / 1_000_000).toFixed(1)} M`;
  if (abs >= 1_000) return `${signe}${(abs / 1_000).toFixed(0)} K`;
  return fmtNombre(n);
};

const fmtDate = (iso: string) => {
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
};

/** Bornes d'un mois plein, au format attendu par l'API (mois : 1-12). */
const bornesDuMois = (annee: number, mois: number): Periode => {
  const dernierJour = new Date(annee, mois, 0).getDate();
  const mm = String(mois).padStart(2, '0');
  return {
    start_date: `${annee}-${mm}-01`,
    end_date: `${annee}-${mm}-${String(dernierJour).padStart(2, '0')}`,
  };
};

const nomDuMois = (annee: number, mois: number) =>
  new Date(annee, mois - 1, 1).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });

/** Décale (annee, mois) de `delta` mois. */
const decalerMois = (annee: number, mois: number, delta: number): [number, number] => {
  const index = annee * 12 + (mois - 1) + delta;
  return [Math.floor(index / 12), (index % 12) + 1];
};

/* ── Une ligne du calcul (libellé / montant / part) ──────────── */
const LigneCalcul: React.FC<{
  libelle: string;
  detail?: string;
  montant: number;
  signe?: '+' | '−';
  couleur: string;
  part: number;
  icone?: React.ReactNode;
}> = ({ libelle, detail, montant, signe, couleur, part, icone }) => (
  <div className="py-2.5">
    <div className="flex items-baseline justify-between gap-4">
      <span className="text-sm text-gray-600 flex items-center gap-2 min-w-0">
        {icone}
        <span className="truncate">{libelle}</span>
      </span>
      <span className="text-sm font-semibold tabular-nums whitespace-nowrap" style={{ color: couleur }}>
        {signe && <span className="mr-0.5">{signe}</span>}
        {fmtNombre(montant)}
      </span>
    </div>
    <div className="mt-1.5 h-1.5 rounded-full bg-gray-100 overflow-hidden">
      <div
        className="h-full rounded-full transition-all"
        style={{ width: `${Math.min(Math.max(part, 0), 100)}%`, background: couleur }}
      />
    </div>
    {detail && <p className="mt-1 text-xs text-gray-400">{detail}</p>}
  </div>
);

/* ── Présentation (sans accès réseau : testable et prévisualisable seule) ── */
export function RecetteNetteVue({
  data,
  onPeriodeChange,
}: {
  data: RecetteNetteData;
  onPeriodeChange?: (periode: Periode) => void;
}) {
  const { recettes, depenses } = data;
  const positif = data.recette_nette >= 0;
  // Les barres du calcul se lisent en part des recettes encaissées
  const part = (montant: number) =>
    recettes.total > 0 ? (montant / recettes.total) * 100 : 0;

  const graphe = data.mois.map((m) => ({
    cle: m.mois,
    mois: m.libelle,
    Recettes: m.recettes_total,
    Dépenses: m.depenses_total,
    'Recette nette': m.recette_nette,
  }));

  // Navigation par mois : le mois affiché est celui du début de période
  const [anneeVue, moisVue] = data.periode.date_debut.split('-').map(Number);
  const aujourdhui = new Date();
  const moisSuivantDisponible =
    anneeVue * 12 + moisVue < aujourdhui.getFullYear() * 12 + (aujourdhui.getMonth() + 1);

  const allerAuMois = (annee: number, mois: number) =>
    onPeriodeChange?.(bornesDuMois(annee, mois));
  const choisirMois = (cle: string) => {
    const [a, m] = cle.split('-').map(Number);
    allerAuMois(a, m);
  };
  const [anneePrec, moisPrec] = decalerMois(anneeVue, moisVue, -1);
  const [anneeSuiv, moisSuiv] = decalerMois(anneeVue, moisVue, 1);

  return (
    <div className="space-y-5">
      {/* Bandeau : le chiffre qui répond à la question */}
      <div
        className="rounded-2xl px-6 py-5 text-white"
        style={{
          background: positif
            ? 'linear-gradient(135deg, #0f766e 0%, #0369a1 100%)'
            : 'linear-gradient(135deg, #b91c1c 0%, #7f1d1d 100%)',
        }}
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-white/70 text-xs uppercase tracking-[0.08em] font-bold">
            Recette nette — espèces + Mobile Money, dépenses déduites
          </p>
          {onPeriodeChange && (
            <div className="flex items-center gap-1 bg-white/10 rounded-xl p-1">
              <button
                onClick={() => allerAuMois(anneePrec, moisPrec)}
                title={`Voir ${nomDuMois(anneePrec, moisPrec)}`}
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-white/90 hover:bg-white/15 transition-colors"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
                <span className="capitalize hidden sm:inline">{nomDuMois(anneePrec, moisPrec)}</span>
              </button>
              <button
                onClick={() => onPeriodeChange({})}
                title="Revenir au mois en cours"
                className="px-2.5 py-1.5 rounded-lg text-xs font-semibold text-white/70 hover:bg-white/15 transition-colors"
              >
                Mois en cours
              </button>
              <button
                onClick={() => moisSuivantDisponible && allerAuMois(anneeSuiv, moisSuiv)}
                disabled={!moisSuivantDisponible}
                title={
                  moisSuivantDisponible
                    ? `Voir ${nomDuMois(anneeSuiv, moisSuiv)}`
                    : 'Pas de mois postérieur au mois en cours'
                }
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-white/90 hover:bg-white/15 transition-colors disabled:opacity-35 disabled:hover:bg-transparent disabled:cursor-not-allowed"
              >
                <span className="capitalize hidden sm:inline">{nomDuMois(anneeSuiv, moisSuiv)}</span>
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-baseline gap-x-8 gap-y-2 mt-2">
          <div className="flex items-baseline gap-2">
            <span className="text-4xl font-black tabular-nums">{fmtShort(data.recette_nette)}</span>
            <span className="text-sm text-white/80">GNF</span>
          </div>
          <div className="text-sm text-white/85">
            {fmtGNF(recettes.total)} encaissés − {fmtGNF(depenses.total)} de dépenses
          </div>
          {recettes.total > 0 && (
            <div className="flex items-center gap-1.5 text-sm text-white/85">
              {positif ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
              {fmtTaux(data.taux_marge)} % des encaissements conservés
            </div>
          )}
        </div>
        <p className="text-white/70 text-xs mt-2">
          Période du {fmtDate(data.periode.date_debut)} au {fmtDate(data.periode.date_fin)}
          {!data.periode.personnalisee && ' (mois en cours)'}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Le calcul, ligne à ligne */}
        <div
          className="bg-white rounded-2xl p-6"
          style={{ border: '1px solid rgba(0,0,0,0.07)', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}
        >
          <p className="text-[11px] font-bold uppercase tracking-[0.08em] text-gray-400 mb-3">
            Le calcul, ligne à ligne <span className="text-gray-300">— en GNF</span>
          </p>

          <LigneCalcul
            libelle="Espèces encaissées"
            detail={
              recettes.especes_autres > 0
                ? `dont ${fmtGNF(recettes.especes_soins)} de soins et ${fmtGNF(recettes.especes_autres)} d'autres entrées`
                : undefined
            }
            montant={recettes.especes}
            signe="+"
            couleur="#0d9488"
            part={part(recettes.especes)}
            icone={<Banknote className="w-3.5 h-3.5 text-teal-600 flex-shrink-0" />}
          />
          <LigneCalcul
            libelle="Mobile Money encaissé"
            detail={
              recettes.mobile_money_autres > 0
                ? `dont ${fmtGNF(recettes.mobile_money_soins)} de soins et ${fmtGNF(recettes.mobile_money_autres)} d'autres entrées`
                : undefined
            }
            montant={recettes.mobile_money}
            signe="+"
            couleur="#f59e0b"
            part={part(recettes.mobile_money)}
            icone={<Smartphone className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />}
          />

          <div className="flex items-baseline justify-between gap-4 py-2.5 border-t border-gray-200 mt-1">
            <span className="text-sm font-semibold text-gray-700">Recettes encaissées</span>
            <span className="text-base font-bold text-gray-900 tabular-nums">
              {fmtNombre(recettes.total)}
            </span>
          </div>

          <LigneCalcul
            libelle="Dépenses journalières"
            detail="Sorties du tiroir-caisse au fil des jours"
            montant={depenses.journalieres}
            signe="−"
            couleur="#ef4444"
            part={part(depenses.journalieres)}
            icone={<ArrowDownRight className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />}
          />
          <LigneCalcul
            libelle="Dépenses mensuelles"
            detail="Salaires, factures fournisseurs — réglées hors caisse journalière"
            montant={depenses.mensuelles}
            signe="−"
            couleur="#6366f1"
            part={part(depenses.mensuelles)}
            icone={<ArrowDownRight className="w-3.5 h-3.5 text-indigo-500 flex-shrink-0" />}
          />

          <div
            className="flex items-baseline justify-between gap-4 mt-2 pt-3 border-t-2"
            style={{ borderColor: '#0f172a' }}
          >
            <span className="text-sm font-bold text-gray-900">Recette nette</span>
            <span
              className="text-xl font-black tabular-nums"
              style={{ color: positif ? '#0f766e' : '#ef4444' }}
            >
              {fmtNombre(data.recette_nette)} GNF
            </span>
          </div>

          {depenses.a_payer.nombre > 0 && (
            <p className="mt-3 pt-3 border-t border-gray-100 text-xs text-gray-500 flex items-start gap-1.5">
              <Clock className="w-3.5 h-3.5 mt-px flex-shrink-0 text-amber-500" />
              <span>
                {depenses.a_payer.nombre} dépense{depenses.a_payer.nombre > 1 ? 's' : ''} engagée
                {depenses.a_payer.nombre > 1 ? 's' : ''} mais pas encore réglée
                {depenses.a_payer.nombre > 1 ? 's' : ''} ({fmtGNF(depenses.a_payer.montant)}) :
                l'argent est encore là, ce montant n'est pas déduit.
              </span>
            </p>
          )}
        </div>

        {/* Où part l'argent */}
        <div
          className="bg-white rounded-2xl p-6"
          style={{ border: '1px solid rgba(0,0,0,0.07)', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}
        >
          <p className="text-[11px] font-bold uppercase tracking-[0.08em] text-gray-400 mb-3">
            Où partent les dépenses
          </p>

          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="rounded-xl p-3" style={{ background: '#fef2f2' }}>
              <p className="text-xs text-red-700/70">Payé depuis la caisse</p>
              <p className="text-lg font-bold text-red-700 tabular-nums mt-0.5">
                {fmtShort(depenses.sur_caisse)}
              </p>
            </div>
            <div className="rounded-xl p-3" style={{ background: '#eef2ff' }}>
              <p className="text-xs text-indigo-700/70">Réglé hors caisse</p>
              <p className="text-lg font-bold text-indigo-700 tabular-nums mt-0.5">
                {fmtShort(depenses.hors_caisse)}
              </p>
            </div>
          </div>

          {depenses.par_categorie.length > 0 ? (
            <ul className="space-y-2.5">
              {depenses.par_categorie.slice(0, 7).map((ligne) => {
                const pourcentage = depenses.total > 0 ? (ligne.montant / depenses.total) * 100 : 0;
                return (
                  <li key={ligne.categorie}>
                    <div className="flex justify-between gap-3 text-sm">
                      <span className="text-gray-600 truncate">{ligne.categorie}</span>
                      <span className="font-medium text-gray-800 tabular-nums whitespace-nowrap">
                        {fmtShort(ligne.montant)}
                        <span className="ml-1.5 text-xs text-gray-400">
                          {Math.round(pourcentage)} %
                        </span>
                      </span>
                    </div>
                    <div className="mt-1 h-1.5 rounded-full bg-gray-100 overflow-hidden">
                      <div className="h-full rounded-full bg-gray-400" style={{ width: `${pourcentage}%` }} />
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="text-sm text-gray-400 py-4 text-center">
              Aucune dépense réglée sur la période.
            </p>
          )}

          <p className="mt-4 pt-3 border-t border-gray-100 text-xs text-gray-400">
            {depenses.nombre} dépense{depenses.nombre > 1 ? 's' : ''} réglée
            {depenses.nombre > 1 ? 's' : ''} sur la période, toutes natures confondues.
          </p>
        </div>
      </div>

      {/* Évolution mensuelle */}
      <div
        className="bg-white rounded-2xl p-6"
        style={{ border: '1px solid rgba(0,0,0,0.07)', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}
      >
        <p className="text-[11px] font-bold uppercase tracking-[0.08em] text-gray-400 mb-4">
          Recette nette mois par mois
        </p>
        {graphe.length === 0 ? (
          <p className="text-center text-gray-400 py-8">Aucun mouvement sur la période.</p>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart
              data={graphe}
              onClick={(e: any) => {
                const cle = e?.activePayload?.[0]?.payload?.cle;
                if (cle) choisirMois(cle);
              }}
              style={onPeriodeChange ? { cursor: 'pointer' } : undefined}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="mois" tick={{ fontSize: 11, fill: '#94a3b8' }} />
              <YAxis tickFormatter={fmtShort} tick={{ fontSize: 12, fill: '#94a3b8' }} />
              <Tooltip formatter={(v: number) => fmtGNF(v)} />
              <Legend />
              {/* Sans animation : le graphe se redessine à chaque changement de mois,
                  et rejouer l'entrée des barres le laisserait vide entre deux clics */}
              <Bar dataKey="Recettes" fill="#0d9488" radius={[4, 4, 0, 0]} isAnimationActive={false} />
              <Bar dataKey="Dépenses" fill="#ef4444" radius={[4, 4, 0, 0]} isAnimationActive={false} />
              <Line
                type="monotone"
                dataKey="Recette nette"
                stroke="#0369a1"
                strokeWidth={2.5}
                dot={{ r: 3, fill: '#0369a1' }}
                isAnimationActive={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Détail mensuel */}
      {data.mois.length > 0 && (
        <div
          className="bg-white rounded-2xl overflow-hidden"
          style={{ border: '1px solid rgba(0,0,0,0.07)', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}
        >
          <p className="text-[11px] font-bold uppercase tracking-[0.08em] text-gray-400 px-6 pt-5 pb-3">
            Détail par mois
          </p>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100">
              <thead className="bg-gray-50">
                <tr className="text-xs font-medium text-gray-500 uppercase">
                  <th className="px-4 py-2 text-left">Mois</th>
                  <th className="px-4 py-2 text-right">Espèces</th>
                  <th className="px-4 py-2 text-right">Mobile Money</th>
                  <th className="px-4 py-2 text-right">Recettes</th>
                  <th className="px-4 py-2 text-right">Dép. journalières</th>
                  <th className="px-4 py-2 text-right">Dép. mensuelles</th>
                  <th className="px-4 py-2 text-right">Recette nette</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {data.mois.map((m) => {
                  const affiche = m.mois === `${anneeVue}-${String(moisVue).padStart(2, '0')}`;
                  return (
                  <tr
                    key={m.mois}
                    onClick={() => choisirMois(m.mois)}
                    title={onPeriodeChange ? `Cadrer la période sur ${m.libelle}` : undefined}
                    className={`hover:bg-gray-50 ${onPeriodeChange ? 'cursor-pointer' : ''} ${
                      affiche ? 'bg-teal-50/70' : ''
                    }`}
                  >
                    <td className="px-4 py-2 text-sm text-gray-700 capitalize whitespace-nowrap">
                      {m.libelle}
                    </td>
                    <td className="px-4 py-2 text-sm text-right tabular-nums text-gray-600">
                      {fmtShort(m.recettes_especes)}
                    </td>
                    <td className="px-4 py-2 text-sm text-right tabular-nums text-gray-600">
                      {fmtShort(m.recettes_mobile_money)}
                    </td>
                    <td className="px-4 py-2 text-sm text-right tabular-nums text-emerald-600 font-medium">
                      {fmtShort(m.recettes_total)}
                    </td>
                    <td className="px-4 py-2 text-sm text-right tabular-nums text-red-600">
                      {fmtShort(m.depenses_journalieres)}
                    </td>
                    <td className="px-4 py-2 text-sm text-right tabular-nums text-indigo-600">
                      {fmtShort(m.depenses_mensuelles)}
                    </td>
                    <td
                      className="px-4 py-2 text-sm text-right tabular-nums font-bold"
                      style={{ color: m.recette_nette >= 0 ? '#0f172a' : '#ef4444' }}
                    >
                      {fmtShort(m.recette_nette)}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <p className="text-xs text-gray-400 px-1">
        {onPeriodeChange && 'Cliquez un mois du graphe ou du tableau pour cadrer la période dessus. '}
        Recettes = encaissements réellement perçus en espèces et Mobile Money — une facture émise
        et non réglée n'y figure pas. Dépenses = celles effectivement payées sur la période.
      </p>
    </div>
  );
}

/* ── Chargement des données ─────────────────────────────────── */
export default function RecetteNette({ dateRange, onPeriodeChange }: Props) {
  const [data, setData] = useState<RecetteNetteData | null>(null);
  const [loading, setLoading] = useState(true);
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get<RecetteNetteData>('/dashboard/analytics/recette-nette/', {
        params: {
          ...(dateRange.start_date ? { start_date: dateRange.start_date } : {}),
          ...(dateRange.end_date ? { end_date: dateRange.end_date } : {}),
          mois: 12,
        },
      })
      .then((r) => { if (!cancelled) { setData(r.data); setErreur(null); } })
      .catch(() => { if (!cancelled) setErreur('Impossible de charger la recette nette.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [dateRange.start_date, dateRange.end_date]);

  if (loading) {
    return (
      <div className="space-y-5">
        <div className="rounded-2xl h-32 bg-gray-100 animate-pulse" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="bg-white rounded-2xl h-64 animate-pulse"
              style={{ border: '1px solid rgba(0,0,0,0.07)' }} />
          ))}
        </div>
      </div>
    );
  }

  if (erreur || !data) {
    return <div className="bg-white rounded-2xl p-8 text-center text-gray-500">{erreur}</div>;
  }

  return <RecetteNetteVue data={data} onPeriodeChange={onPeriodeChange} />;
}
